import React from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineSwap } from "react-icons/ai";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import OButton from "components/reusable/OButton";
import OSearch from "components/reusable/OSearch";
import ODatePickerWithRange from "components/shared/datePicker/ODatePickerWithRange";
import dayjs from "dayjs";

const ShiftFiltersHeader = ({
  // View Mode
  viewMode,
  setViewMode,
  onSwitchToCalendar,
  // Calendar Controls
  calendarView,
  setCalendarView,
  calendarDate,
  setCalendarDate,
  handleCalendarNav,
  goToToday,
  getCalendarDateRange,
  // Filters
  searchTerm,
  setSearchTerm,
  filterData,
  setFilterData,
  setPage,
  // Dropdowns Data
  allShiftsList,
  departments,
  locations,
  selectedShift,
  setSelectedShift,
  departmentId,
  setDepartmentId,
  // Modals Toggles
  setShowSwapRequests,
  setShowTemplateModal,
  setShiftAdd,
  setShowLocationModal,
  // Reset & Date Change
  handleReset,
  handleDateChange,
  // Translation
  t
}) => {
  
  // Wrapper: Syncs Filter Date to Calendar when date picker changes
  const onDateChangeWrapper = (start, end) => {
    handleDateChange(start, end);
    if (start) {
      setCalendarDate(dayjs(start));
    }
  };

  return (
    <>
      {/* ═══ TOP ROW - Action Buttons & Toggles ═══ */}
      <div className="border-b border-b-[#E3E3E3] px-3 py-2">
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          
          <div className="flex border rounded-lg mr-auto">
            <button
              type="button"
              className={`px-4 py-2 text-sm ${
                viewMode === "list"
                  ? "bg-LightBlue text-white"
                  : "bg-white text-gray-600"
              }`}
              onClick={() => setViewMode("list")}
            >
              {t("LIST")}
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm ${
                viewMode === "calendar"
                  ? "bg-LightBlue text-white"
                  : "bg-white text-gray-600"
              }`}
              onClick={() => {
                if (onSwitchToCalendar) onSwitchToCalendar();
                setViewMode("calendar");
              }}
            >
              {t("CALENDAR")}
            </button>
          </div>

          {/* Manage Locations Button */}
          <button
            type="button"
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-green-300 bg-green-50 text-green-600 hover:bg-green-100 whitespace-nowrap"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t("MANAGE_LOCATIONS")}
          </button>

          {/* Swap Request Button */}
          <button
            type="button"
            onClick={() => setShowSwapRequests(true)}
            className="relative flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-orange-300 bg-orange-50 text-orange-600 hover:bg-orange-100 whitespace-nowrap"
          >
            <AiOutlineSwap className="w-3.5 h-3.5" />
            {t("SWAP_REQUESTS")}
          </button>

          {/* Templates Button */}
          <button
            type="button"
            onClick={() => setShowTemplateModal(true)}
            className="flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100 whitespace-nowrap"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            {t("TEMPLATES")}
          </button>

          {/* Create Shift Button */}
          <button
            title={t("CREATE_SHIFT")}
            type="button"
            className="bg-LightBlue flex text-xs px-3 py-2 rounded-md items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap"
            onClick={() => setShiftAdd(true)}
          >
            + {t("CREATE_SHIFT")}
          </button>
        </div>
      </div>

      {/* ═══ MIDDLE ROW - Calendar Controls (Only visible in Calendar View) ═══ */}
      {viewMode === "calendar" && (
        <div className="border-b border-b-[#E3E3E3] px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleCalendarNav(-1)}
                className="p-2 border rounded hover:bg-gray-50"
              >
                <FaChevronLeft className="w-3 h-3" />
              </button>
              
              <button
                onClick={goToToday}
                className="px-4 py-2 border rounded hover:bg-gray-50 text-sm font-medium"
              >
                {t("TODAY")}
              </button>

              <button
                onClick={() => handleCalendarNav(1)}
                className="p-2 border rounded hover:bg-gray-50"
              >
                <FaChevronRight className="w-3 h-3" />
              </button>

              <h3 className="text-lg font-semibold ml-2">
                {getCalendarDateRange()}
              </h3>
            </div>

            <div className="flex border rounded-lg">
              <button
                type="button"
                className={`px-4 py-2 text-sm ${
                  calendarView === "daily"
                    ? "bg-LightBlue text-white"
                    : "bg-white text-gray-600"
                }`}
                onClick={() => setCalendarView("daily")}
              >
                {t("DAILY")}
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm ${
                  calendarView === "weekly"
                    ? "bg-LightBlue text-white"
                    : "bg-white text-gray-600"
                }`}
                onClick={() => setCalendarView("weekly")}
              >
                {t("WEEKLY")}
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm ${
                  calendarView === "monthly"
                    ? "bg-LightBlue text-white"
                    : "bg-white text-gray-600"
                }`}
                onClick={() => setCalendarView("monthly")}
              >
                {t("MONTHLY")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ BOTTOM ROW - Filters ═══ */}
      <div className="px-4 py-4">
        {/* Flex container to keep all filters on the same row */}
        <div className="flex flex-wrap items-center gap-3">
          
          {/* Search */}
          <div className="relative flex-grow min-w-[180px]">
            <OSearch
              searchTerm={searchTerm}
              placeholder={t("Search by name and ID")}
              setSearchTerm={(value) => {
                setSearchTerm(value);
                setFilterData({ ...filterData, searchKey: value });
              }}
            />
          </div>

          {/* All Shifts Dropdown */}
          <div className="flex-shrink-0">
            <select
              className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedShift(value);
                setFilterData({ ...filterData, shiftId: value });
                setPage(1);
              }}
              value={filterData?.shiftId || ""}
            >
              <option value="">{t("ALL_SHIFTS")}</option>
              {allShiftsList?.map((shift) => (
                <option key={shift._id} value={shift._id}>
                  {shift.name}
                </option>
              ))}
            </select>
          </div>

          {/* All Departments Dropdown */}
          <div className="flex-shrink-0">
            <select
              className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
              onChange={(e) => {
                const value = e.target.value;
                setDepartmentId(value);
                setFilterData({ ...filterData, departmentId: value });
                setPage(1);
              }}
              value={filterData?.departmentId || ""}
            >
              <option value="">{t("ALL_DEPARTMENTS")}</option>
              {departments?.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* All Locations Dropdown */}
          <div className="flex-shrink-0">
            <select
              className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
              onChange={(e) =>
                setFilterData({ ...filterData, locationId: e.target.value })
              }
              value={filterData?.locationId}
            >
              <option value="">{t("ALL_LOCATIONS")}</option>
              {locations?.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          {/* All Status Dropdown */}
          <div className="flex-shrink-0">
            <select
              className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
              onChange={(e) =>
                setFilterData({ ...filterData, status: e.target.value })
              }
              value={filterData?.status}
            >
              <option key='' value="">{t("ALL_STATUS")}</option>
              <option key='draft' value="draft">{t("DRAFT")}</option>
              <option key='published' value="published">{t("PUBLISHED")}</option>
              <option key='open' value="open">{t("OPEN")}</option>
              <option key='assigned' value="assigned">{t("ASSIGNED")}</option>
            </select>
          </div>

          {/* ═══ DATE PICKER - HIDDEN IN CALENDAR MODE ═══ */}
          {viewMode === "list" && (
            <div className="flex-shrink-0">
              <ODatePickerWithRange
                handleDateChange={onDateChangeWrapper}
                isReset={filterData?.isReset}
                setIsReset={(val) => setFilterData({ ...filterData, isReset: val })}
              />
            </div>
          )}

          {/* Reset Button */}
          <div className="flex-shrink-0">
            <OButton
              onClick={handleReset}
              extraClasses=""
              label={t("RESET")}
              type="button"
              style={{ borderRadius: "0.5rem" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ShiftFiltersHeader;